import React, { useContext, createContext, useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from 'react-query';
import { useSessionRegistration } from 'common/components/TouchpointEditor/hooks/useSessionRegistration';
import { getVirtualSessionsApiClient } from 'lib/SplashApiClient/subclients/VirtualSessionsApiClient';
import { getSessionApiClient } from 'lib/SplashApiClient/subclients/SessionApiClient';
import { camelize } from 'utils/textFormats';
import { useGuestAccessHash } from 'utils/hooks/useGuestAccessHash';
import { getRegisteredSessions } from '../utils/index';
import { getDate, getTime } from '../utils/dateUtils';
import { initialSessionsContext } from './initialSessionContext';
import { useFilteredSessions } from '../hooks/UseFilteredSessions';

const SessionsContext = createContext(initialSessionsContext);

export const SessionContextProvider = ({ children, context, eventId, isHostView }) => {
    const sessionsApiClient = useRef(getVirtualSessionsApiClient()).current;
    const { guestAccessHash } = useGuestAccessHash(`sessionsRegistration/${eventId}/guestAccessHash`);
    const hasGuestAccessHash = !!guestAccessHash;

    const isPublicData = isHostView || !hasGuestAccessHash;

    const guestDataQuery = useQuery(
        ['guestSessionsData', eventId, guestAccessHash],
        () => sessionsApiClient.getEventListSessions({ eventId, guestAccessHash }),
        {
            enabled: !isPublicData,
        },
    );

    const sessionsDataQuery = useQuery(
        ['sessionsData', eventId],
        () =>
            isHostView
                ? getSessionApiClient().getSessions(
                      eventId,
                      {
                          limit: 100,
                          page: 1,
                      },
                      ['SessionManagerPublic', 'SessionDatesWithTimezone'],
                      { sort_field: 'start', sort_order: 'ASC' },
                  )
                : sessionsApiClient.getPublicSessions({ eventId }),
        {
            select: (response) => (isHostView ? camelize(response.data) : response),
            enabled: isPublicData,
        },
    );

    const { data = [], isLoading, isError } = isPublicData ? sessionsDataQuery : guestDataQuery;

    const [tagsFilter, setTagsFilter] = useState(context.filters.tags);
    const [searchTermFilter, setSearchTermFilter] = useState(context.filters.searchTerm);
    const [datesFilter, setDatesFilter] = useState(context.filters.dates);
    const [startTimesFilter, setStartTimesFilter] = useState(context.filters.startTimes);
    const [locationsFilter, setLocationsFilter] = useState(context.filters.locations);
    const [eventListsFilter, setEventListsFilter] = useState(context.filters.eventLists);

    const sessionRegistration = useSessionRegistration();
    const registeredSessions = getRegisteredSessions(sessionRegistration, data);

    const filteredData = useFilteredSessions(
        data,
        tagsFilter,
        searchTermFilter,
        datesFilter,
        startTimesFilter,
        locationsFilter,
        eventListsFilter,
        registeredSessions,
    );
    const filteredGuestSessionsData = useFilteredSessions(
        registeredSessions,
        tagsFilter,
        searchTermFilter,
        datesFilter,
        startTimesFilter,
        locationsFilter,
        eventListsFilter,
        registeredSessions,
    );

    // Get only the unique tags from the sessions list based on id
    const uniqueTags = useMemo(() => [...new Set(data.flatMap((session) => session.tags))], [data]);
    const uniqueDates = useMemo(() => [...new Set(data.map((session) => getDate(session.start)))], [data]);
    const uniqueStartTimes = useMemo(() => [...new Set(data.map((session) => getTime(session.start)))], [data]);
    // The Location filter is only visible for In-Person multi-session events and if there is at least one session that
    // has a location field.
    const uniqueLocations = useMemo(
        () => [
            ...new Set(
                data
                    .map((session) => (session.types?.includes('in-person') ? session.location : false))
                    .filter(Boolean),
            ),
        ],
        [data],
    );

    const value = {
        data,
        filteredData,
        filteredGuestSessionsData,
        registeredSessions,
        filters: {
            tags: tagsFilter,
            searchTerm: searchTermFilter,
            dates: datesFilter,
            startTimes: startTimesFilter,
            locations: locationsFilter,
            eventLists: eventListsFilter,
            setTags: setTagsFilter,
            setSearchTerm: setSearchTermFilter,
            setDates: setDatesFilter,
            setStartTimes: setStartTimesFilter,
            setLocations: setLocationsFilter,
            setEventLists: setEventListsFilter,
        },
        isLoading,
        isError,
        uniqueTags,
        uniqueDates,
        uniqueStartTimes,
        uniqueLocations,
    };

    return <SessionsContext.Provider value={value}>{children}</SessionsContext.Provider>;
};

SessionContextProvider.propTypes = {
    children: PropTypes.node,
    context: PropTypes.object,
    eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    isHostView: PropTypes.bool,
};

SessionContextProvider.defaultProps = {
    children: null,
    context: initialSessionsContext,
};

export const useSessions = () => useContext(SessionsContext);
